/**
 * useQuote — debounced live-quote hook.
 *
 * Given an input amount and direction, fires `fetchQuote` against the
 * Worker proxy, debouncing rapid input changes and aborting in-flight
 * requests when a newer one starts. Returns the quote, loading state,
 * and any error. Re-fetches automatically when the inputs change and
 * every `refreshMs` (default 10 s) when idle so prices stay fresh.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { fetchQuote, type QuoteResponse } from "@/lib/api";
import type { TokenSymbol } from "@/lib/tokens";
import { directionString } from "@/lib/tokens";

interface UseQuoteArgs {
  amount: string;
  inputToken: TokenSymbol;
  /** Debounce delay in ms. Default 300. */
  debounceMs?: number;
  /** Auto-refresh interval in ms when the input is stable. Default 10_000. */
  refreshMs?: number;
  /** Set to false to pause fetching (e.g. while the user is typing). */
  enabled?: boolean;
}

interface UseQuoteResult {
  quote: QuoteResponse | null;
  loading: boolean;
  error: Error | null;
  refetch: () => void;
}

export function useQuote({
  amount,
  inputToken,
  debounceMs = 300,
  refreshMs = 10_000,
  enabled = true,
}: UseQuoteArgs): UseQuoteResult {
  const [quote, setQuote] = useState<QuoteResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Refs hold the "latest intent" so we can cancel a stale fetch when
  // a new one starts. useState would cause extra renders.
  const abortRef = useRef<AbortController | null>(null);
  const reqIdRef = useRef(0);

  const run = useCallback(async () => {
    // Empty or zero input: clear the quote and bail.
    if (!enabled || !amount || amount === "0" || amount === "0." || amount === ".") {
      setQuote(null);
      setError(null);
      setLoading(false);
      return;
    }

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    const id = ++reqIdRef.current;

    setLoading(true);
    setError(null);
    try {
      const result = await fetchQuote(amount, directionString(inputToken), {
        signal: controller.signal,
      });
      // Only publish if this is still the latest request.
      if (id === reqIdRef.current) {
        setQuote(result);
        setLoading(false);
      }
    } catch (err) {
      if ((err as Error).name === "AbortError") return;
      if (id === reqIdRef.current) {
        setError(err as Error);
        setLoading(false);
      }
    }
  }, [amount, inputToken, enabled]);

  // Debounced fetch on input change.
  useEffect(() => {
    const t = setTimeout(run, debounceMs);
    return () => clearTimeout(t);
  }, [run, debounceMs]);

  // Idle refresh so the quote stays live while the user is thinking.
  useEffect(() => {
    if (!enabled || !amount || loading) return;
    const t = setInterval(run, refreshMs);
    return () => clearInterval(t);
  }, [run, enabled, amount, loading, refreshMs]);

  // Clean up on unmount.
  useEffect(() => () => abortRef.current?.abort(), []);

  return { quote, loading, error, refetch: run };
}
